/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import {
  emptySelectionModel,
  isSelectionModelExcludeMode,
  isSelectionModelHas,
  SelectionModel,
  selectionModelMarkedCount,
  selectionModelSelect,
  selectionModelSelectOnly,
} from '@gh/core-data';

import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { assign, isFunction, isNil, property, sortedIndexBy, stubFalse } from 'lodash';

import {
  GRID_COLUMN_FEATURE,
  GRID_FEATURE,
  GridColumnFeature,
  GridFeature,
  GridFeatureInitParams,
  GridFeatureReadyParams,
} from '../feature.interfaces';
import { GridMultipleSelectorComponent } from './grid-multiple-selector.component';
import { GridSelectAllHeaderComponent } from './grid-select-all-header.component';
import { GridSelection, GridSelector } from './grid-selectable.interfaces';
import { GridSingleSelectorComponent } from './grid-single-selector.component';
import { GridPaginationCursor } from '../../state';
import { ID } from '../../../common-shared-data';

export type GridSelectionMode = 'single' | 'multiple';

function addSelector(selectors: GridSelector[], id: any, selector: GridSelector): void {
  // @ts-ignore
  selectors.splice(sortedIndexBy(selectors, { id }, 'id'), 0, selector);
}

function removeSelector(selectors: GridSelector[], id: any): void {
  // @ts-ignore
  selectors.splice(sortedIndexBy(selectors, { id }, 'id'), 1);
}

function getSelector(selectors: GridSelector[], id: any): Maybe<GridSelector> {
  // @ts-ignore
  const index = sortedIndexBy(selectors, { id }, 'id');

  if (index >= 0) {
    return selectors[index];
  } else {
    return void 0;
  }
}

@Directive({
  selector: 'gh-grid[ghGridSelectable], gh-grid-column[ghGridSelectable]',
  providers: [
    { provide: GRID_FEATURE, useExisting: GridSelectableDirective, multi: true },
    { provide: GRID_COLUMN_FEATURE, useExisting: GridSelectableDirective, multi: true },
  ],
})
export class GridSelectableDirective implements OnInit, OnChanges, GridFeature, GridColumnFeature, GridSelection {
  @Input() selectionMode: GridSelectionMode = 'multiple';
  @Input() selectAll = false;
  @Input() selectAllWidth = 40;
  @Input() cursor: GridPaginationCursor;
  @Input() outsideHandle: boolean;
  @Input() checkList: any[] = [];
  @Input() loadedData: any[] = [];
  // tslint:disable-next-line:no-input-prefix
  @Input() isCanBeSelected: Function;
  @Output() selectionModelChange = new EventEmitter<SelectionModel>();
  // tslint:disable-next-line:no-output-on-prefix
  @Output() onToggleSelectAll = new EventEmitter<boolean>();
  // tslint:disable-next-line:no-output-on-prefix
  @Output() onClickSelectAll = new EventEmitter();
  // tslint:disable-next-line:no-output-on-prefix
  @Output() onCheckChange = new EventEmitter<ID>();

  private getRowNodeId: (data: any) => any;

  private selectors: GridSelector[] = [];
  private allSelector?: GridSelector;
  private _selectionModel: SelectionModel = emptySelectionModel();
  private initialized = false;
  private _rowDisabled: PredicateFn<any> = stubFalse;
  private _disabled: boolean = false;
  private api: GridApi;
  private cursorState: {
    page: number;
    pageSize: number;
    sort: any;
  };

  @Input('ghGridSelectableDisabled')
  set disabled(disabled: boolean) {
    this._disabled = disabled;

    this.updateSelectors();
    this.updateAllSelector();
  }

  @Input('ghGridSelectableRowDisabled')
  set isRowDisabled(disabled: PredicateFn<any> | string) {
    this._rowDisabled = isFunction(disabled) ? disabled : property(disabled);
  }

  @Input()
  set selectionModel(model: SelectionModel) {
    if (model !== this._selectionModel) {
      this._selectionModel = model || emptySelectionModel();

      this.updateSelectors();
      this.updateAllSelector();
    }
  }

  ngOnInit(): void {
    this.initialized = true;
    if (this.cursor) {
      this.cursor.changes.subscribe((state) => {
        if (!this.cursorState ||
          state.page !== this.cursorState.page ||
          state.pageSize !== this.cursorState.pageSize ||
          state.sort !== this.cursorState.sort) {
          this.toggleSelectAll(false);
          this.cursorState = {
            page: state.page,
            pageSize: state.pageSize,
            sort: state.sort,
          };
        }
      });
    }

    this.updateAllSelector();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.checkList) {
      this.updateAllSelector();
      this.updateSelectors();
    }
  }

  initGrid(params: GridFeatureInitParams): void {
    // nothing to do
  }

  enhanceGridOptions(options: GridOptions): void {
    const columnDefs = options.columnDefs = options.columnDefs || [];

    // create common selector column
    const colDef: ColDef = this.createCommonSelectorColumnDef();
    this.mergeSelectorColumnDef(colDef);
    columnDefs.unshift(colDef);

    this.initRowNodeId(options);
  }

  enhanceColumn(colDef: ColDef): void {
    this.mergeSelectorColumnDef(colDef);
  }

  afterGridOptionsEnhanced(options: GridOptions): void {
    this.initRowNodeId(options);
  }

  onGridReady(params: GridFeatureReadyParams): void {
    this.api = params.api;
  }

  onDestroy(): void {
    // nothing to do
  }

  isSelected(id: any): boolean {
    return this.outsideHandle ? this.checkList.includes(id)
      : isSelectionModelHas(this._selectionModel, id) && (!isFunction(this._rowDisabled) || !this._rowDisabled({id}));
  }

  isDisabled(): boolean {
    return this._disabled;
  }

  isAllSelected(): boolean {
    if (this.outsideHandle) {
      return this.checkList.length > 0 && this.checkList.length === this.loadedData.length;
    }
    return  isSelectionModelExcludeMode(this._selectionModel);
  }

  isSelectable(data: any): boolean {
    if (!!this.isCanBeSelected) {
      return this.isCanBeSelected(data) && !this._disabled && !this._rowDisabled(data);
    }
    return !this._disabled && !this._rowDisabled(data);
  }

  isSelectedPartially(): boolean {
    if (this.outsideHandle) {
      const length = this.checkList.length;
      return length > 0 && length < this.loadedData.length;
    }
    return this._selectionModel.idList.length > 0;
  }

  isOutsideHandle(): boolean {
    return this.outsideHandle;
  }

  toggleSelectAll(selected?: boolean): void {
    if (this.outsideHandle) {
      return;
    }
    if (isNil(selected) ? this.isAllSelected() : selected) {
      this.setSelectionModel({ isExcludeMode: true, idList: [] });
    } else {
      this.setSelectionModel({ isExcludeMode: false, idList: [] });
    }

    this.updateSelectors();
    this.updateAllSelector();
  }

  clickSelectAll(): void {
    if (this.outsideHandle) {
      this.onClickSelectAll.emit();
      this.updateSelectors();
      this.updateAllSelector();
      return;
    }
    this.toggleSelectAll(!this.isAllSelected());
  }

  select(id: any, selected: boolean, model?: any): void {
    if (this.isSingleMode()) {
      if (this.outsideHandle) {
        this.onCheckChange.emit(id);
      } else {
        this.setSelectionModel(selectionModelSelectOnly(this._selectionModel, id, selected));
      }
      this.updateSelectors();
    } else {
      // if we select/unselect all rows => invert mode of selection model and update all selector
      if (this.outsideHandle) {
        this.onCheckChange.emit(id);
        this.updateAllSelector();
      } else {
        const selectionModel =  selectionModelSelect(this._selectionModel, id, selected);
        if (this.api) {
          if (selectionModelMarkedCount(selectionModel) === this.api.getModel().getRowCount()) {
            this.setSelectionModel(emptySelectionModel(!isSelectionModelExcludeMode(selectionModel)));
            this.updateAllSelector();
          } else {
            this.setSelectionModel(selectionModel);
          }
        } else {
          this.setSelectionModel(selectionModel);
        }
      }
      this.updateSelector(id);
    }

    this.updateAllSelector();
  }

  addSelector(id: any, selector: GridSelector): void {
    addSelector(this.selectors, id, selector);
  }

  removeSelector(id: any): void {
    removeSelector(this.selectors, id);
  }

  setAllSelector(selector: GridSelector): void {
    this.allSelector = selector;
  }

  removeAllSelector(): void {
    this.allSelector = void 0;
  }

  private initRowNodeId(options: GridOptions): void {
    const { getRowNodeId } = options;

    if (isNil(getRowNodeId)) {
      throw new Error('getRowNodeId is not defined on GridOptions');
    }

    this.getRowNodeId = getRowNodeId;
  }

  private updateSelectors(): void {
    this.selectors.forEach((selector) => selector.refreshSelection());
  }

  private updateSelector(id: any): void {
    const selector = getSelector(this.selectors, id);

    if (selector) {
      selector.refreshSelection();
    }
  }

  private updateAllSelector(): void {
    if (this.allSelector) {
      this.allSelector.refreshSelection();
    }
  }

  private setSelectionModel(selectionModel: SelectionModel): void {
    if (this._selectionModel !== selectionModel) {
      this._selectionModel = selectionModel;
      this.selectionModelChange.emit(selectionModel);
    }
  }

  private isSingleMode(): boolean {
    return this.selectionMode === 'single';
  }

  private createCommonSelectorColumnDef(): ColDef {
    return {
      colId: 'sys:selector',
      pinned: 'left',
      headerName: '',
      sortable: false,
      resizable: false,
      suppressMovable: true,
      width: this.selectAllWidth,
      headerClass: 'grid-selector-cell',
      cellClass: 'grid-selector-cell',
    };
  }

  private mergeSelectorColumnDef(colDef: ColDef): void {
    if (this.selectionMode === 'single') {
      assign(colDef, {
        cellRendererFramework: GridSingleSelectorComponent,
        cellRendererParams: { selection: this },
      });
    } else {
      assign(colDef, {
        headerComponentFramework: this.selectAll ? <any>GridSelectAllHeaderComponent : void 0,
        headerComponentParams: this.selectAll ? { selection: this } : void 0,
        cellRendererFramework: GridMultipleSelectorComponent,
        cellRendererParams: { selection: this },
      });
    }
  }
}
